import { useMemo, useState } from 'react';
import CompanionCard from '@/components/companion/CompanionCard';
import { Button, Card } from '@/components/ui';
import { facetsOf, useCompanions, type Companion } from '@/lib/data/companions';

type Availability = 'all' | 'online';

const LABEL = 'eyebrow mb-2 block';
const FIELD =
  'w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3.5 py-2.5 text-sm text-[var(--ink)] transition-colors duration-150 hover:border-[var(--marigold)] focus:border-[var(--marigold)]';

export default function Directory() {
  const { companions, loading, error } = useCompanions();

  // Defaults to "everyone" because presence is Phase 3 — filtering to online
  // would show an empty page and look broken.
  const [availability, setAvailability] = useState<Availability>('all');
  const [language, setLanguage] = useState('');
  const [service, setService] = useState('');
  const [city, setCity] = useState('');

  const {
    languages: ALL_LANGUAGES,
    services: ALL_SERVICES,
    cities: ALL_CITIES,
  } = useMemo(() => facetsOf(companions), [companions]);

  const results = useMemo(
    () =>
      companions
        .filter((c) => {
          if (availability === 'online' && !c.isOnline) return false;
          if (language && !c.languages.includes(language)) return false;
          if (city && c.city !== city) return false;

          // Filtered on what they actually offer. This used to check the
          // free-text `specialties` array, which nothing has written to since
          // the pivot — so choosing anything returned nobody.
          if (service && !c.services.some((s) => s.categorySlug === service)) return false;
          return true;
        })
        .sort((a: Companion, b: Companion) => {
          // Free now, then busy, then offline — then by rating.
          const rank = (x: Companion) => (x.isOnline && !x.isBusy ? 0 : x.isOnline ? 1 : 2);
          return rank(a) - rank(b) || b.rating - a.rating;
        }),
    [companions, availability, language, service, city],
  );

  const isFiltered = availability !== 'all' || language !== '' || service !== '' || city !== '';

  const reset = () => {
    setAvailability('all');
    setLanguage('');
    setService('');
    setCity('');

  };

  if (loading) {
    return (
      <div className="py-20 text-center">
        <p className="text-sm text-[var(--muted)]">Loading companions…</p>
      </div>
    );
  }

  // A failed load must never render as "nobody matches" — an empty directory
  // and a broken query have to look different.
  if (error) {
    return (
      <Card className="mx-auto max-w-md px-6 py-16 text-center">
        <h1 className="text-xl font-semibold" style={{ color: 'var(--danger)' }}>
          Could not load companions
        </h1>
        <p className="mt-2 text-sm text-[var(--ink-2)]">{error}</p>
        <div className="mt-6">
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
      </Card>
    );
  }

  if (companions.length === 0) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <h1 className="text-2xl font-semibold">No companions yet</h1>
        <p className="mt-3 text-[var(--ink-2)]">
          Nobody has been approved yet. Once an application is accepted they appear here
          automatically.
        </p>
      </div>
    );
  }

  return (
    <div>
      <header className="animate-rise">
        <p className="eyebrow">Directory</p>
        <h1 className="mt-2 text-4xl font-semibold">Find someone to talk to</h1>
        <p className="mt-3 max-w-[54ch] text-[var(--ink-2)]">
          Everyone here is over 18 and identity-verified. Rates are per minute, and you are only
          charged for the time you actually spend.
        </p>
      </header>

      <Card raised className="animate-rise mt-8 p-5" style={{ animationDelay: '80ms' }}>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <span className={LABEL}>Availability</span>
            <div className="flex rounded-xl border border-[var(--line)] p-1">
              {(['online', 'all'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setAvailability(v)}
                  aria-pressed={availability === v}
                  className="flex-1 rounded-lg px-2 py-1.5 text-sm font-medium transition-all duration-150"
                  style={
                    availability === v
                      ? {
                          background: 'var(--marigold)',
                          color: '#fff',
                          boxShadow: 'inset 0 1px 0 rgb(255 255 255 / 0.18)',
                        }
                      : { color: 'var(--ink-2)' }
                  }
                >
                  {v === 'online' ? 'Online' : 'Everyone'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={LABEL} htmlFor="language">
              Language
            </label>
            <select
              id="language"
              className={FIELD}
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="">Any language</option>
              {ALL_LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={LABEL} htmlFor="service">
              Looking for
            </label>
            <select
              id="service"
              className={FIELD}
              value={service}
              onChange={(e) => setService(e.target.value)}
            >
              <option value="">Anything</option>
              {ALL_SERVICES.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={LABEL} htmlFor="city">
              City
            </label>
            <select
              id="city"
              className={FIELD}
              value={city}
              onChange={(e) => setCity(e.target.value)}
            >
              <option value="">Anywhere</option>
              {ALL_CITIES.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>

          {/* A single coins/min slider used to live here. It cannot work now:
              a rate belongs to a (service, channel) pair, and the units differ
              — per minute for calls, per hour in person. One number cannot
              filter both. Price filtering belongs on the service browse page,
              where the unit is known. */}
        </div>
      </Card>

      <div className="mt-8 mb-5 flex items-baseline justify-between gap-4">
        <p className="text-sm text-[var(--muted)]">
          <span className="font-semibold text-[var(--ink)] tabular-nums">{results.length}</span>{' '}
          {results.length === 1 ? 'person' : 'people'}
        </p>
        {isFiltered && (
          <button
            type="button"
            onClick={reset}
            className="text-sm font-medium text-[var(--marigold)] underline-offset-4 transition-colors hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {results.length > 0 ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {results.map((c, i) => (
            <CompanionCard key={c.id} companion={c} index={i} />
          ))}
        </div>
      ) : (
        <Card className="animate-rise px-6 py-16 text-center">
          <div
            className="mx-auto grid size-14 place-items-center rounded-2xl"
            style={{ background: 'var(--surface-2)' }}
          >
            <svg viewBox="0 0 24 24" className="size-6 text-[var(--muted)]" fill="none" aria-hidden>
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
              <path
                d="M16.5 16.5L21 21"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <h2 className="mt-5 text-xl font-semibold">Nobody matches those filters</h2>
          <p className="mx-auto mt-2 max-w-[44ch] text-sm text-[var(--ink-2)]">
            Try another language, or raise the rate a little. Switching to Everyone will show people
            who are offline right now but taking calls later today.
          </p>
          <div className="mt-6">
            <Button onClick={reset} variant="secondary">
              Clear filters
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
